// Module 11 — the Help / About panel content (Wave 3 of the UI/UX overhaul). Pure data + a pure DOM builder
// via the UiDocument seam (headless-testable, like legend.ts). This is the simulator's manual: what it is, how
// to drive it, what the layers/charts/map mean, and a couple of guided experiments. NON-CONTRACT seam.
import type { UiDocument, UiElement } from "./seam";

interface HelpSection { title: string; body: string[] } // body = paragraph/bullet lines

export const HELP_SECTIONS: HelpSection[] = [
  { title: "What this is", body: [
    "A live simulation of the Greater Toronto Area power grid — 2,120 buses, 3,556 lines, 151 generators and 449 neighbourhoods, solved every tick as a real DC power flow and dispatched by merit order.",
    "Everything runs in your browser; nothing is pre-recorded. Change any control and the whole grid re-solves — demand, dispatch, prices, flows, frequency and money all update.",
  ] },
  { title: "Driving the simulation", body: [
    "▶ Play / Pause and the 1×–10× buttons set the clock. Each tick = one simulated hour, so demand and prices sweep the daily curve.",
    "The Hour and Temperature sliders reshape the grid live (even while paused). Heat is the key one: it BOTH drives demand (AC / heating) AND derates the grid (lines and gas turbines lose capacity when hot).",
    "Season and Weekday / Weekend change the demand shape.",
    "Scenarios jump the whole operating point to an anchor (winter peak, summer heatwave…); Event presets inject a disturbance — a nuclear trip, a cyberattack on comms, or a feeder fault that FLISR self-heals.",
  ] },
  { title: "The three layers", body: [
    "ENERGY — the power grid itself: lines coloured by voltage tier, warming amber→red as they load up, with flow arrows sized by MW.",
    "INFORMATION — the comms / SCADA overlay (blue arced links). A cyber or comms fault degrades it independently of the power grid.",
    "FINANCE — the money overlay: every served consumer pays a 3-leg retail bill (commodity → generator, transmission → Hydro One, distribution → the local utility), drawn as gold-arrowed arcs.",
    "Toggle any layer from the Controls panel; each simulates independently, so you can turn two off and study one.",
  ] },
  { title: "Reading the map", body: [
    "Open the Legend panel for the full colour key. In short: a line's colour is its voltage tier and it WARMS toward red as it loads; arrow size = power flow; grey = de-energized or switched open.",
    "Neighbourhood parcels glow yellow when stressed, dim when shed, and go grey when dark (blacked out).",
  ] },
  { title: "The charts", body: [
    "The bar across the top opens ~25 charts, grouped by topic — Supply & Demand, Economics, Frequency & Stability, Voltage, Reserves & Security, Losses, Storage & Hydro, and Protection.",
    "Hover any chip (or the chart's title) to see what it shows. Many charts read flat/healthy at steady state on purpose — the Frequency, Protection and UFLS charts come alive only when you inject a fault.",
  ] },
  { title: "Inspecting objects", body: [
    "Click any bus, line, plant or neighbourhood on the map to open the Inspector — its live flow / voltage / dispatch, a single-line diagram, its list of connections (each with a breaker you can open or close to reroute power), and, for a consumer, its itemised bill.",
  ] },
  { title: "Try this", body: [
    "Open Scenarios → “Nuclear plant failure (Darlington)”. Watch the frequency dip, the reserves and BESS deploy, imports ramp up, and — if the loss is big enough — load shed in priority order and then recover.",
    "Or drag Temperature toward +40 °C on a summer day: demand climbs, the lines and gas plants derate, losses rise, and the reserve margin shrinks — the grid visibly strains.",
  ] },
];

export function buildHelp(doc: UiDocument, host: UiElement): void {
  const cards: UiElement[] = [];
  const intro = doc.createElement("div");
  intro.className = "help-intro";
  intro.textContent = "A quick guide to the GTA smart-grid simulator. Hover any button or chart for a one-line explanation; open the Legend for the map key.";
  cards.push(intro);
  for (const s of HELP_SECTIONS) {
    const card = doc.createElement("div");
    card.className = "help-card";
    const h = doc.createElement("div");
    h.className = "help-h";
    h.textContent = s.title;
    const rows: UiElement[] = [h];
    for (const line of s.body) {
      const p = doc.createElement("div");
      p.className = "help-p";
      p.textContent = line;
      rows.push(p);
    }
    card.replaceChildren(...rows);
    cards.push(card);
  }
  host.replaceChildren(...cards);
}
